// feature/inline-accionante-edit — case-detail surface for the
// existing PATCH /api/personas/:id endpoint. Lives on the case
// detail's primary "Accionante" <dd> so users (typically reparto
// during intake / triage) can fix a typo where they discover it
// instead of navigating to /personas. Backed by the SAME endpoint
// + audit shape as the Personas page edit; PERSONA_EDIT_ROLES is
// imported from views/Personas.js so the role list stays single-
// source.
//
// Sibling-count guard: if the persona is linked to >1 active case,
// the save flow surfaces a confirmation hint before PATCHing —
// because the rename propagates to every case using that persona,
// which is the right semantics but worth confirming visibly.
import React, { useEffect, useState } from "react";
import { personas as personasApi } from "../api/endpoints";
import { useAuth } from "../auth/AuthContext";
import { rolesOf } from "../utils/permissions";
import { PERSONA_EDIT_ROLES } from "../views/Personas";
import Spinner from "./Spinner";
import { useToast } from "./Toast";

export default function InlineEditAccionante({ c, onUpdated }) {
  const { user } = useAuth();
  const toast = useToast();
  const personaId = c?.persona_id;
  const personaName = c?.accionante_nombre || "";
  const cedula = c?.cedula;

  const canEdit = PERSONA_EDIT_ROLES.some(r => rolesOf(user).includes(r));

  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(personaName);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { siblings, attempted_value }
  const [savedFlash, setSavedFlash] = useState(false);

  // Reset local state if the underlying case row swaps to a different
  // persona (e.g. the modal is reused for a different case).
  useEffect(() => {
    if (!editing) setVal(personaName);
  }, [personaName, editing]);

  function startEdit() {
    setVal(personaName);
    setErr(null);
    setPendingConfirm(null);
    setEditing(true);
  }
  function cancel() {
    setEditing(false);
    setErr(null);
    setPendingConfirm(null);
  }

  // Two-phase save. First click checks siblings; if any exist,
  // promotes to confirm step. Second click (or first when no
  // siblings) does the actual PATCH.
  async function save() {
    setErr(null);
    if (!val || !val.trim()) {
      setErr("El nombre no puede estar vacío.");
      return;
    }
    if (val.trim() === personaName.trim()) {
      cancel();
      return;
    }
    if (!personaId) {
      setErr("Caso sin accionante vinculado.");
      return;
    }

    // Phase 1 — sibling probe (skipped on the confirm-step click).
    if (!pendingConfirm) {
      setBusy(true);
      try {
        const r = await personasApi.cases(personaId);
        const siblings = (r.data || []).filter(x => x.id !== c.id).length;
        if (siblings > 0) {
          setPendingConfirm({ siblings, attempted_value: val.trim() });
          setBusy(false);
          return;
        }
      } catch (_) {
        // If the sibling probe fails, fall through to the save —
        // worst case we apply the rename without the warning.
      }
      setBusy(false);
    }

    // Phase 2 — actual PATCH.
    await doPatch();
  }

  async function doPatch() {
    setBusy(true);
    setErr(null);
    try {
      await personasApi.update(personaId, { nombre: val.trim() });
      setEditing(false);
      setPendingConfirm(null);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      // Tell the case-detail wrapper to refetch — picks up the new
      // accionante_nombre via the LEFT JOIN in cases.js GET /:id.
      onUpdated?.();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Error al guardar.";
      setErr(msg);
      toast.push(msg, "error");
    } finally {
      setBusy(false);
    }
  }

  // Plain text for non-editors (insumos, anonymous, role-less).
  if (!canEdit) {
    return (
      <span>
        {personaName || "—"}
        {cedula && <span className="muted"> · CC {cedula}</span>}
      </span>
    );
  }

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span>{personaName || "—"}</span>
        {cedula && <span className="muted">· CC {cedula}</span>}
        <button
          type="button"
          className="ghost sm"
          style={{ fontSize: 11, padding: "2px 8px" }}
          onClick={startEdit}
          title="Editar nombre del accionante"
        >Editar</button>
        {savedFlash && (
          <span style={{ color: "var(--accent-mint)", fontSize: 12 }} aria-hidden="true">✓</span>
        )}
      </span>
    );
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          autoFocus
          disabled={busy}
          style={{ minWidth: 240 }}
          maxLength={200}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") cancel();
          }}
        />
        {!pendingConfirm ? (
          <>
            <button type="button" disabled={busy} onClick={save} style={{ fontSize: 12, padding: "2px 10px" }}>
              {busy && <Spinner color="currentColor" />} Guardar
            </button>
            <button type="button" className="secondary" disabled={busy} onClick={cancel} style={{ fontSize: 12, padding: "2px 10px" }}>
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button type="button" disabled={busy} onClick={doPatch} style={{ fontSize: 12, padding: "2px 10px" }}>
              {busy && <Spinner color="currentColor" />} Confirmar
            </button>
            <button type="button" className="secondary" disabled={busy} onClick={cancel} style={{ fontSize: 12, padding: "2px 10px" }}>
              Cancelar
            </button>
          </>
        )}
      </div>
      {pendingConfirm && (
        <div className="warn-inline" style={{
          padding: 8, fontSize: 12,
          background: "var(--color-warning-bg)",
          borderColor: "var(--color-warning)",
          color: "var(--text-body)",
        }}>
          Este accionante también aparece en {pendingConfirm.siblings} otro(s) caso(s). El cambio se aplicará a todos.
        </div>
      )}
      {err && (
        <div style={{ fontSize: 11, color: "var(--color-danger)" }}>{err}</div>
      )}
    </div>
  );
}
